import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';

const LanguageSelector = ({ className = '' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { 
      code: 'es', 
      name: 'Español', 
      flag: '🇲🇽',
      shortName: 'ES'
    },
    { 
      code: 'en', 
      name: 'English', 
      flag: '🇺🇸',
      shortName: 'EN'
    }
  ];

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors border border-white/20"
        data-testid="language-selector"
      >
        <span className="text-xl">{currentLang.flag}</span>
        <span className="text-white font-medium text-sm">{currentLang.shortName}</span>
        <ChevronDown className={`w-4 h-4 text-white/70 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-[#1a1a2e] border border-purple-500/30 rounded-lg shadow-xl overflow-hidden z-50 min-w-[140px]">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-500/20 transition-colors ${
                currentLang.code === lang.code ? 'bg-purple-500/30' : ''
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="text-white text-sm">{lang.name}</span>
              {currentLang.code === lang.code && (
                <span className="ml-auto text-purple-400">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Versión compacta para móvil (solo toggle)
export const LanguageToggle = ({ className = '' }) => {
  const { i18n } = useTranslation();
  
  const isSpanish = i18n.language === 'es' || i18n.language.startsWith('es-');
  
  const toggleLanguage = () => {
    i18n.changeLanguage(isSpanish ? 'en' : 'es');
  };

  return (
    <button
      onClick={toggleLanguage}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors border border-white/20 ${className}`}
      data-testid="language-toggle"
      title={isSpanish ? 'Switch to English' : 'Cambiar a Español'}
    >
      <span className="text-base">{isSpanish ? '🇲🇽' : '🇺🇸'}</span>
      <span className="text-white font-semibold text-xs">{isSpanish ? 'ES' : 'EN'}</span>
    </button>
  );
};

export default LanguageSelector;
